import React, { useState, useContext } from 'react';
import {
  Button,
  Typography,
  Tooltip,
  Icon,
  Icons,
  styled,
} from '@nextiva/next-ui-components';
import { formattedLicensePricingDataType } from 'types/purchaseLicenseModalTypes';
import { LicensesContext } from '../../../containers/pages/BillingLicenses/licensesContext/index';

const StyledDetailsShell = styled('div')`
  width: 100%;
  height: 100%;
  border: 1px solid;
  border-color: ${({ theme }) => theme.palette.secondary.dark};
  border-radius: 8px;
  margin-bottom: 24px;
  padding: 16px;
`;
const StyledDetailsButton = styled(Button)`
  font-weight: 400;
  font-size: ${({ theme }) => theme.typography.pxToRem(12)};
  line-height: 16px;
  margin: 4px 0px 20px 72px;
`;
const StyledSectionLableText = styled(Typography)`
  line-height: 20px;
  color: ${({ theme }) => theme.palette.secondary.contrastText};
`;
const StyledSectionContainer = styled('div')`
  margin-top: 15px;
  display: flex;
  justify-content: space-between;
`;
const StyledText = styled(Typography)`
  color: ${({ theme }) => theme.palette.secondary.contrastText};
  overflow-wrap: break-word;
`;
const StyledFlexItem = styled('div')`
  display: flex;
`;
const StyledHelpIcon = styled(Icon)`
  fill: ${({ theme }) => theme.palette.blueGrey.A400};
  transform: scale(0.75);
  margin: 3px 0px 0px 10px;
`;
const StyledDivider = styled('div')`
  border: 1px solid ${({ theme }) => theme.palette.secondary.main};
  margin: 32px 0px;
`;
const StyledMonthlyChargesContainer = styled('div')`
  display: flex;
  justify-content: space-between;
`;

const CURRENT_CHARGES_TOOLTIP_TEXT =
  'The total current charges do not include taxes and fees. Those will be applied in your next invoice.';
const ADJUSTMENT_TOOLTIP_TEXT =
  'The prorated monthly amount is based on the date of purchase.';

interface IShowBillingDetails {
  quantity: string;
  formattedLicensePricingData: formattedLicensePricingDataType;
  didInitialLicensePricingDataApiCallFail: boolean;
}

export const ShowBillingDetails = ({
  quantity,
  formattedLicensePricingData,
  didInitialLicensePricingDataApiCallFail,
}: IShowBillingDetails) => {
  const {
    licensesState: { purchaseLicenseAssignment },
  } = useContext(LicensesContext);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  const licenseName = purchaseLicenseAssignment?.name;
  const licenseDisplayName = licenseName.includes('NUC-')
    ? purchaseLicenseAssignment?.license_definition?.description ??
      purchaseLicenseAssignment?.licenseDefinition?.description
    : licenseName;

  return showDetails ? (
    <>
      <StyledDetailsButton
        color="primary"
        size="medium"
        variant="text"
        onClick={() => setShowDetails(false)}
        data-testid="PlatformAdminUI-PurchaseLicenseModal-HideDetailsButton"
      >
        Hide details
      </StyledDetailsButton>
      <StyledDetailsShell data-testid="PlatformAdminUI-PurchaseLicenseModal-BillingDetails">
        <StyledSectionLableText variant="button">
          Recurring monthly charges
          <StyledSectionContainer>
            <StyledFlexItem>
              <StyledText variant="body2">Total current charges</StyledText>
              <Tooltip placement="top" title={CURRENT_CHARGES_TOOLTIP_TEXT}>
                <StyledHelpIcon
                  icon={Icons.help}
                  aria-label="Total current charges help"
                  data-testid="PlatformAdminUI-PurchaseLicenseModal-TotalCurrentChargesHelpIcon"
                />
              </Tooltip>
            </StyledFlexItem>
            <StyledText variant="body2">
              {formattedLicensePricingData?.totalCurrentMonthlyCharges}
            </StyledText>
          </StyledSectionContainer>
        </StyledSectionLableText>
        <StyledDivider />
        <StyledSectionLableText variant="button">
          New charges
          <StyledSectionContainer>
            <StyledText variant="body2">{licenseDisplayName}</StyledText>
            <StyledText variant="body2">
              ( {Number(quantity)} x {formattedLicensePricingData?.licensePrice}{' '}
              )&nbsp;&nbsp;&nbsp;
              {
                formattedLicensePricingData?.newRecurringChargesWithoutTaxesAndFees
              }
            </StyledText>
          </StyledSectionContainer>
        </StyledSectionLableText>
        <StyledDivider />
        <StyledMonthlyChargesContainer>
          <StyledSectionLableText variant="button">
            Total new monthly charges
          </StyledSectionLableText>
          <StyledSectionLableText variant="button">
            {formattedLicensePricingData?.totalNewMonthlyCharges}
          </StyledSectionLableText>
        </StyledMonthlyChargesContainer>
        <StyledDivider />
        <StyledSectionLableText variant="button">
          Prorated Charges
          <StyledSectionContainer>
            <StyledFlexItem>
              <StyledText variant="body2">Prorated monthly amount</StyledText>
              <Tooltip placement="top" title={ADJUSTMENT_TOOLTIP_TEXT}>
                <StyledHelpIcon
                  icon={Icons.help}
                  aria-label="Prorated monthly adjustment help"
                  data-testid="PlatformAdminUI-PurchaseLicenseModal-ProratedMonthlyAdjustmentHelpIcon"
                />
              </Tooltip>
            </StyledFlexItem>
            <StyledText variant="body2">
              ( {Number(quantity)} x{' '}
              {formattedLicensePricingData?.proratedLicensePrice}
              )&nbsp;&nbsp;&nbsp;
              {formattedLicensePricingData?.proratedMonthlyAdjustment}
            </StyledText>
          </StyledSectionContainer>
        </StyledSectionLableText>
      </StyledDetailsShell>
    </>
  ) : (
    <StyledDetailsButton
      color="primary"
      size="medium"
      variant="text"
      disabled={didInitialLicensePricingDataApiCallFail}
      onClick={() => setShowDetails(true)}
      data-testid="PlatformAdminUI-PurchaseLicenseModal-ShowDetailsButton"
    >
      Show details
    </StyledDetailsButton>
  );
};
